import React, { useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Collapse,
  Table,
  Alert,
} from "reactstrap";
import { IsValidPermissions } from "Routes/AuthProtected";
import {
  useGetProductsSupplierByIdQuery,
  useGetPruchaseOrdersByProductAndSupplierMutation,
  useGetStockManagementQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";
import PriceViewComponent from "./PriceViewComponent";

type SectionKeys = "lowest_cost" | "previous_orders_mapped";

interface OpenSectionsState {
  lowest_cost: boolean;
  previous_orders_mapped: boolean;
}

interface OrderItem {
  po_number: string;
  po_id: number;
  po_status: string;
  po_created_date: string;
  item_id: number;
  product_id: number;
  product_name: string;
  ordered_quantity: number;
  delivered_quantity: number;
  accepted_quantity: number;
  item_status: string;
  delivery_status: string;
  expected_delivery_date: string;
  actual_delivery_date: string;
  unit_price: number;
  total_price: number;
}

interface OrderData {
  supplier: {
    id: number;
    name: string;
  };
  product: {
    id: number;
    name: string;
    code: string | null;
  };
  // Backward-compatible field name from old API (now equals total_orders_count)
  last_3_orders_count: number;
  // New pagination meta from API
  total_orders_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  orders: Array<{
    po_id: number;
    po_number: string;
    po_status: string;
    created_date: string;
    total_cost: number;
    items: OrderItem[];
  }>;
}

export interface SupplierSelectionModalProps {
  isOpen: boolean;
  toggle: () => void;
  /** Product to base supplier pricing/history on */
  productId: number | null | undefined;
  /** Optional product name for headers */
  productName?: string;
  /** Optional current supplier id to highlight */
  currentSupplierId?: number | null;
  /** Optional target project id for filtering stock transfer rows */
  targetProjectId?: number | string | null;
  /** Optional requested quantity to cap stock allocation qty */
  requestedQty?: number | null;
  /** Callback when user confirms selection */
  onSelect: (supplier: any) => void;
}

const SupplierSelectionModal: React.FC<SupplierSelectionModalProps> = ({
  isOpen,
  toggle,
  productId,
  productName,
  currentSupplierId,
  targetProjectId,
  requestedQty,
  onSelect,
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [selectedStockSource, setSelectedStockSource] = useState<any>(null);
  const [stockQty, setStockQty] = useState<string>("");

  // Order history state
  const [orderHistoryModal, setOrderHistoryModal] = useState(false);
  const [selectedSupplierForHistory, setSelectedSupplierForHistory] =
    useState<any>(null);
  const [orderHistoryData, setOrderHistoryData] = useState<OrderData | null>(
    null,
  );
  const [orderHistoryPage, setOrderHistoryPage] = useState<number>(1);
  const [orderHistoryPageSize] = useState<number>(5);

  // Collapsible sections state
  const [openSections, setOpenSections] = useState<OpenSectionsState>({
    lowest_cost: true,
    previous_orders_mapped: true,
  });

  // Fetch product suppliers
  const {
    data: productSuppliersData,
    isLoading: productSuppliersLoading,
    isError: productSuppliersError,
  } = useGetProductsSupplierByIdQuery(productId as number, {
    skip: !productId || !isOpen,
  });

  // Fetch purchase orders for selected supplier and product using mutation
  const [
    getPurchaseOrdersByProductAndSupplier,
    { isLoading: orderHistoryLoading, isError: orderHistoryError },
  ] = useGetPruchaseOrdersByProductAndSupplierMutation();
  const { data: stockManagementData, isLoading: stockManagementLoading } =
    useGetStockManagementQuery(
      { page: 1, pageSize: 1000, search: "" },
      {
        skip: !isOpen || !productId,
      },
    );

  const toggleSection = (sectionKey: SectionKeys) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleOrderHistoryModal = async (supplier?: any) => {
    if (supplier) {
      setSelectedSupplierForHistory(supplier);
      setOrderHistoryPage(1);
      if (productId) {
        try {
          const result = await getPurchaseOrdersByProductAndSupplier({
            supplier_id: supplier.supplier_id,
            product_id: productId,
            page: 1,
            page_size: orderHistoryPageSize,
          }).unwrap();
          setOrderHistoryData(result as OrderData);
        } catch (error) {
          // Keep simple: swallow and rely on error state
          // eslint-disable-next-line no-console
          console.error("Failed to load order history:", error);
        }
      }
      setOrderHistoryModal(true);
    } else {
      setOrderHistoryModal(false);
    }
  };

  const refetchOrderHistory = async () => {
    if (selectedSupplierForHistory && productId) {
      try {
        const result = await getPurchaseOrdersByProductAndSupplier({
          supplier_id: selectedSupplierForHistory.supplier_id,
          product_id: productId,
          page: orderHistoryPage,
          page_size: orderHistoryPageSize,
        }).unwrap();
        setOrderHistoryData(result as OrderData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to refetch order history:", error);
      }
    }
  };

  // Group suppliers by filter type
  const groupedSuppliers = useMemo(() => {
    if (!productSuppliersData?.suppliers) return {};

    const groups: any = {};

    productSuppliersData.suppliers.forEach((supplier: any) => {
      // Skip suppliers without available price
      if (!supplier.total_amount || supplier.total_amount <= 0) {
        return;
      }

      if (!groups[supplier.filter_by]) {
        groups[supplier.filter_by] = [];
      }

      // Check if supplier already exists in this group to avoid duplicates
      const exists = groups[supplier.filter_by].find(
        (s: any) => s.supplier_id === supplier.supplier_id,
      );

      if (!exists) {
        groups[supplier.filter_by].push({
          ...supplier,
          finalAmount: supplier.final_cost || 0,
          hasPositiveAmount: (supplier.total_amount || 0) > 0,
        });
      }
    });

    return groups;
  }, [productSuppliersData]);

  const getGroupDisplayName = (filterBy: string) => {
    const displayNames: Record<string, string> = {
      lowest_cost: "Lowest Cost Suppliers",
      previous_orders_mapped: "Suppliers with Order History",
    };
    return (
      displayNames[filterBy] ||
      filterBy.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const getGroupDescription = (filterBy: string) => {
    const descriptions: Record<string, string> = {
      lowest_cost: "Suppliers offering the best prices for this product",
      previous_orders_mapped: "Suppliers you've successfully worked with before",
    };
    return descriptions[filterBy] || "";
  };

  // Process suppliers for recommendation (across all groups)
  const processedSuppliers = useMemo(() => {
    const allSuppliers = productSuppliersData?.suppliers || [];

    // Filter suppliers with total_amount > 0 first
    const suppliersWithPositiveAmount = allSuppliers.filter(
      (supplier: any) => (supplier.total_amount || 0) > 0,
    );

    const suppliersWithFinalPrice = suppliersWithPositiveAmount.map(
      (supplier: any) => ({
        ...supplier,
        finalAmount: supplier.final_cost || 0,
      }),
    );

    // Find the best price (lowest final amount) only from suppliers with total_amount > 0
    const bestPrice =
      suppliersWithPositiveAmount.length > 0
        ? Math.min(...suppliersWithPositiveAmount.map((s: any) => s.finalAmount))
        : null;

    // Mark suppliers with best price as recommended (only if they have total_amount > 0)
    return suppliersWithFinalPrice.map((supplier: any) => ({
      ...supplier,
      isRecommended:
        bestPrice !== null &&
        supplier.finalAmount === bestPrice &&
        (supplier.total_amount || 0) > 0,
      hasPositiveAmount: (supplier.total_amount || 0) > 0,
    }));
  }, [productSuppliersData]);

  const isSupplierRecommended = (supplier: any) => {
    return processedSuppliers.find(
      (s: any) => s.supplier_id === supplier.supplier_id && s.isRecommended,
    );
  };

  const handleSupplierClick = (supplier: any) => {
    setSelectedSupplier(supplier);
    setSelectedStockSource(null);
    setStockQty("");
  };

  const stockRows = useMemo(() => {
    const rows = Array.isArray(stockManagementData)
      ? stockManagementData
      : stockManagementData?.results || [];
    return rows.filter((row: any) => {
      if (Number(row?.product) !== Number(productId)) return false;
      const balanceQty = Number(row?.balance_quantity || 0);
      if (balanceQty <= 0) return false;
      if (targetProjectId == null || targetProjectId === "") return true;
      const isCrossProject =
        String(row?.source_project) !== String(targetProjectId);
      if (!isCrossProject) return true;
      return !!row?.is_transfer_to_another_project;
    });
  }, [productId, stockManagementData, targetProjectId]);

  const stockQtyMax = useMemo(() => {
    if (!selectedStockSource) return 0;
    const balance = Number(selectedStockSource.balance_quantity || 0);
    const requested =
      requestedQty != null && Number.isFinite(Number(requestedQty))
        ? Number(requestedQty)
        : null;
    if (requested == null) return balance;
    return Math.max(0, Math.min(balance, requested));
  }, [requestedQty, selectedStockSource]);

  const stockQtyNum = Number(stockQty || 0);

  const handleStockSelect = (stock: any) => {
    setSelectedStockSource(stock);
    setSelectedSupplier(null);
    setStockQty("");
  };

  const hasSuppliersWithPrices = Object.keys(groupedSuppliers).length > 0;

  const formatDateDisplay = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTimeDisplay = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleConfirm = () => {
    if (selectedStockSource) {
      if (stockQtyNum <= 0) {
        return;
      }
      onSelect({
        ...selectedStockSource,
        source_mode: "stock",
        stock_source_id: selectedStockSource.id,
        stock_qty: stockQtyNum,
      });
      toggle();
      return;
    }
    if (!selectedSupplier) {
      return;
    }
    onSelect({
      ...selectedSupplier,
      source_mode: "supplier",
    });
    toggle();
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Select Supplier{productName ? ` for ${productName}` : ""}
        </ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <h6 className="mb-2">Stock Sources</h6>
            {stockManagementLoading ? (
              <div className="text-muted">
                <Spinner size="sm" className="me-2" />
                Loading stock sources...
              </div>
            ) : stockRows.length === 0 ? (
              <div className="text-muted">
                No stock sources available for this product.
              </div>
            ) : (
              <div className="border rounded">
                {stockRows.map((stock: any) => {
                  const isSelected = selectedStockSource?.id === stock.id;
                  const isCrossProject =
                    targetProjectId != null &&
                    targetProjectId !== "" &&
                    String(stock?.source_project) !== String(targetProjectId);
                  return (
                    <div
                      key={stock.id}
                      className={`p-3 border-bottom cursor-pointer ${
                        isSelected ? "border-primary bg-soft-primary" : ""
                      }`}
                      onClick={() => handleStockSelect(stock)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-semibold">
                            {stock.stock_reference_number || `Stock ${stock.id}`}
                          </div>
                          <div className="text-muted fs-12">
                            Balance: {stock.balance_quantity}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {isCrossProject && (
                            <Badge color="warning">Cross Project</Badge>
                          )}
                          {isSelected && (
                            <i className="ri-checkbox-circle-fill text-primary fs-18"></i>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-2">
                          <div className="text-muted fs-12 mb-1">
                            Stock allocation quantity
                          </div>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min={0}
                            max={stockQtyMax}
                            value={stockQty}
                            onChange={(e) => {
                              const raw = Number(e.target.value || 0);
                              const clamped = Math.min(
                                stockQtyMax,
                                Math.max(0, raw),
                              );
                              setStockQty(
                                e.target.value === "" ? "" : String(clamped),
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="text-muted fs-12 mt-1">
                            Max: {stockQtyMax}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {productSuppliersLoading ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
              <div className="mt-2 text-muted">Loading suppliers...</div>
            </div>
          ) : productSuppliersError ? (
            <div className="text-center text-muted py-3">
              <i className="ri-error-warning-line fs-18"></i>
              <div>Failed to load suppliers</div>
            </div>
          ) : hasSuppliersWithPrices ? (
            <div className="mb-4">
              <h6 className="mb-3">Available Suppliers</h6>

              <div className="suppliers-list">
                {Object.keys(groupedSuppliers).map((filterBy) => {
                  const sectionKey = filterBy as SectionKeys;

                  return (
                    <div key={filterBy} className="mb-4">
                      <Card className="border-0 shadow-sm">
                        <CardHeader
                          className="bg-light cursor-pointer"
                          onClick={() => toggleSection(sectionKey)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0 text-primary">
                                {getGroupDisplayName(filterBy)}
                                <i
                                  className={`ri-arrow-${
                                    openSections[sectionKey] ? "down" : "right"
                                  }-s-line ms-2`}
                                ></i>
                              </h6>
                              <small className="text-muted">
                                {getGroupDescription(filterBy)}
                              </small>
                            </div>
                            <Badge color="outline-primary">
                              {groupedSuppliers[filterBy].length} supplier
                              {groupedSuppliers[filterBy].length !== 1
                                ? "s"
                                : ""}
                            </Badge>
                          </div>
                        </CardHeader>
                        <Collapse isOpen={openSections[sectionKey]}>
                          <CardBody className="p-0">
                            {groupedSuppliers[filterBy].map((supplier: any) => (
                              <div
                                key={`${supplier.supplier_id}-${supplier.mapping_id}`}
                                className={`p-3 border-bottom cursor-pointer ${
                                  selectedSupplier?.supplier_id ===
                                  supplier.supplier_id
                                    ? "border-primary bg-soft-primary"
                                    : "border-light"
                                } ${
                                  currentSupplierId === supplier.supplier_id
                                    ? "border-success bg-soft-success"
                                    : ""
                                }`}
                                onClick={() => handleSupplierClick(supplier)}
                              >
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center mb-2">
                                      <h6 className="mb-0 me-2">
                                        {supplier.supplier_name}
                                      </h6>
                                      {isSupplierRecommended(supplier) && (
                                        <Badge
                                          color="success"
                                          className="fs-11"
                                        >
                                          <i className="ri-thumb-up-line me-1"></i>
                                          Recommended
                                        </Badge>
                                      )}
                                      {supplier.hasPositiveAmount && (
                                        <Badge color="info" className="fs-11">
                                          <i className="ri-money-dollar-circle-line me-1"></i>
                                          Price Available
                                        </Badge>
                                      )}
                                      {currentSupplierId ===
                                        supplier.supplier_id && (
                                        <Badge color="info" className="fs-11">
                                          <i className="ri-check-line me-1"></i>
                                          Current Supplier
                                        </Badge>
                                      )}
                                      {supplier.has_previous_orders && (
                                        <div className="d-flex align-items-center">
                                          <Badge
                                            color="warning"
                                            className="fs-11 me-1"
                                          >
                                            <i className="ri-history-line me-1"></i>
                                            {supplier.order_count} Previous
                                            Order
                                            {supplier.order_count !== 1
                                              ? "s"
                                              : ""}
                                          </Badge>
                                          <Button
                                            color="link"
                                            size="sm"
                                            className="p-0 ms-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleOrderHistoryModal(supplier);
                                            }}
                                            title="View Order History"
                                          >
                                            <i className="ri-information-line fs-16 text-info"></i>
                                          </Button>
                                        </div>
                                      )}
                                    </div>

                                    <div className="text-muted small mb-2">
                                      {supplier.supplier_email && (
                                        <div className="d-flex align-items-center">
                                          <i className="ri-mail-line me-1"></i>
                                          {supplier.supplier_email}
                                        </div>
                                      )}
                                      {supplier.supplier_phone && (
                                        <div className="d-flex align-items-center">
                                          <i className="ri-phone-line me-1"></i>
                                          {supplier.supplier_phone}
                                        </div>
                                      )}
                                    </div>

                                    {IsValidPermissions([
                                      "VIEW_ALL_PURCHASE_COST",
                                    ]) ? (
                                      <div className="price-info">
                                        <div className="d-flex align-items-center flex-wrap">
                                          {supplier.total_amount ? (
                                            <>
                                              <span className="text-dark fw-medium me-2">
                                                Unit Price: $
                                                {supplier.finalAmount.toFixed(
                                                  2,
                                                )}
                                              </span>
                                              {supplier.discount > 0 && (
                                                <span className="text-success fs-12">
                                                  <s className="text-muted me-1">
                                                    $
                                                    {supplier.total_amount.toFixed(
                                                      2,
                                                    )}
                                                  </s>
                                                  {supplier.discount}% off
                                                </span>
                                              )}
                                            </>
                                          ) : (
                                            <span className="text-muted fst-italic">
                                              Price not available
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="price-info">
                                        <Alert color="warning" className="fs-11">
                                          <i className="ri-lock-line me-1"></i>
                                          Price View Restricted
                                        </Alert>
                                      </div>
                                    )}
                                  </div>

                                  <div className="d-flex flex-column align-items-end">
                                    {selectedSupplier?.supplier_id ===
                                      supplier.supplier_id && (
                                      <i className="ri-checkbox-circle-fill text-primary fs-18 mb-2"></i>
                                    )}
                                    {isSupplierRecommended(supplier) && (
                                      <Badge
                                        color="outline-success"
                                        className="fs-10"
                                      >
                                        Best Price
                                      </Badge>
                                    )}
                                    {supplier.supplier_rating && (
                                      <div className="mt-1">
                                        <small className="text-warning">
                                          {Array.from(
                                            { length: 5 },
                                            (_, i) => (
                                              <i
                                                key={i}
                                                className={`ri-star-${
                                                  i < supplier.supplier_rating
                                                    ? "fill"
                                                    : "line"
                                                } fs-12`}
                                              ></i>
                                            ),
                                          )}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </CardBody>
                        </Collapse>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center text-muted py-3">
              <i className="ri-information-line fs-18"></i>
              <div>No suppliers with available prices for this product</div>
              <small className="text-muted">
                All suppliers either don't have pricing information or prices
                are not available.
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={
              (!selectedSupplier && !selectedStockSource) ||
              (selectedStockSource && stockQtyNum <= 0)
            }
          >
            {selectedStockSource ? "Use Stock" : "Select Supplier"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Order History Modal */}
      <Modal
        isOpen={orderHistoryModal}
        toggle={() => toggleOrderHistoryModal()}
        size="xl"
      >
        <ModalHeader toggle={() => toggleOrderHistoryModal()}>
          <i className="ri-history-line me-2"></i>
          Order History - {selectedSupplierForHistory?.supplier_name}
          {productName ? ` & ${productName}` : ""}
        </ModalHeader>
        <ModalBody>
          {orderHistoryLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">Loading order history...</div>
            </div>
          ) : orderHistoryError ? (
            <div className="text-center py-5">
              <i className="ri-error-warning-line fs-48 text-danger"></i>
              <div className="mt-2 text-danger">Failed to load order history</div>
              <Button
                color="primary"
                className="mt-3"
                onClick={refetchOrderHistory}
              >
                Try Again
              </Button>
            </div>
          ) : orderHistoryData ? (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <Card className="border-0 bg-soft-info">
                    <CardBody>
                      <h6 className="text-info mb-1">
                        <i className="ri-user-line me-2"></i>
                        Supplier
                      </h6>
                      <div className="fw-bold">
                        {orderHistoryData.supplier.name}
                      </div>
                      <small className="text-muted">
                        ID: {orderHistoryData.supplier.id}
                      </small>
                    </CardBody>
                  </Card>
                </div>
                <div className="col-md-6">
                  <Card className="border-0 bg-soft-success">
                    <CardBody>
                      <h6 className="text-success mb-1">
                        <i className="ri-box-line me-2"></i>
                        Product
                      </h6>
                      <div className="fw-bold">
                        {orderHistoryData.product.name}
                      </div>
                      <small className="text-muted">
                        {orderHistoryData.product.code &&
                          `Code: ${orderHistoryData.product.code} • `}
                        ID: {orderHistoryData.product.id}
                      </small>
                    </CardBody>
                  </Card>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0">Purchase Orders</h6>
                  <small className="text-muted">
                    Page {orderHistoryData.page} of {orderHistoryData.total_pages}
                  </small>
                </div>
                <Badge color="primary">
                  {orderHistoryData.total_orders_count} Orders Found
                </Badge>
              </div>

              {orderHistoryData.orders.length > 0 ? (
                orderHistoryData.orders.map((order: any) => (
                  <Card
                    key={order.po_id}
                    className="mb-3 border-0 shadow-sm"
                  >
                    <CardHeader className="bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0 text-primary">
                            PO: {order.po_number}
                          </h6>
                          <small className="text-muted">
                            Created: {formatDateTimeDisplay(order.created_date)}
                          </small>
                        </div>
                        <div className="text-end">
                          <Badge
                            color={
                              order.po_status === "delivered"
                                ? "success"
                                : order.po_status === "submitted"
                                  ? "warning"
                                  : order.po_status === "draft"
                                    ? "secondary"
                                    : "info"
                            }
                          >
                            {order.po_status.toUpperCase()}
                          </Badge>
                          <div className="text-success fw-bold mt-1">
                            <PriceViewComponent
                              price={order.total_cost?.toFixed(2)}
                            />
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>Item Status</th>
                            <th>Ordered Qty</th>
                            <th>Delivered Qty</th>
                            <th>Accepted Qty</th>
                            <th>Delivery Status</th>
                            <th>Expected Delivery</th>
                            <th>Actual Delivery</th>
                            <th>Unit Price</th>
                            <th>Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((orderItem: OrderItem) => (
                            <tr key={orderItem.item_id}>
                              <td>
                                <Badge
                                  color={
                                    orderItem.item_status === "delivered"
                                      ? "success"
                                      : orderItem.item_status === "pending"
                                        ? "warning"
                                        : "info"
                                  }
                                >
                                  {orderItem.item_status}
                                </Badge>
                              </td>
                              <td className="fw-bold">
                                {orderItem.ordered_quantity}
                              </td>
                              <td>{orderItem.delivered_quantity}</td>
                              <td>{orderItem.accepted_quantity}</td>
                              <td>
                                <Badge
                                  color={
                                    orderItem.delivery_status === "delivered"
                                      ? "success"
                                      : orderItem.delivery_status ===
                                          "partially_delivered"
                                        ? "warning"
                                        : orderItem.delivery_status ===
                                            "in_transit"
                                          ? "info"
                                          : "secondary"
                                  }
                                >
                                  {orderItem.delivery_status || "N/A"}
                                </Badge>
                              </td>
                              <td>
                                {orderItem.expected_delivery_date
                                  ? formatDateDisplay(
                                      orderItem.expected_delivery_date,
                                    )
                                  : "N/A"}
                              </td>
                              <td>
                                {orderItem.actual_delivery_date
                                  ? formatDateDisplay(
                                      orderItem.actual_delivery_date,
                                    )
                                  : "N/A"}
                              </td>
                              <td>
                                {(() => {
                                  // Calculate unit price if not provided or is 0
                                  let unitPrice = orderItem.unit_price;
                                  if (!unitPrice || unitPrice === 0) {
                                    const totalPrice = parseFloat(
                                      orderItem.total_price?.toString() || "0",
                                    );
                                    const orderedQty = parseFloat(
                                      orderItem.ordered_quantity
                                        ?.toString() || "0",
                                    );
                                    if (orderedQty > 0) {
                                      unitPrice = totalPrice / orderedQty;
                                    } else {
                                      unitPrice = 0;
                                    }
                                  }
                                  return (
                                    <PriceViewComponent
                                      price={unitPrice?.toFixed(2)}
                                    />
                                  );
                                })()}
                              </td>
                              <td className="fw-bold text-success">
                                <PriceViewComponent
                                  price={orderItem.total_price?.toFixed(2)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="ri-information-line fs-48 text-muted"></i>
                  <div className="mt-2 text-muted">No previous orders found</div>
                  <small className="text-muted">
                    This supplier hasn't delivered this product before.
                  </small>
                </div>
              )}
              {orderHistoryData.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted small">
                    Showing page {orderHistoryData.page} of {orderHistoryData.total_pages}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="light"
                      disabled={orderHistoryPage <= 1 || orderHistoryLoading}
                      onClick={async () => {
                        if (orderHistoryPage <= 1 || !selectedSupplierForHistory || !productId) return;
                        const nextPage = orderHistoryPage - 1;
                        setOrderHistoryPage(nextPage);
                        try {
                          const result = await getPurchaseOrdersByProductAndSupplier({
                            supplier_id: selectedSupplierForHistory.supplier_id,
                            product_id: productId,
                            page: nextPage,
                            page_size: orderHistoryPageSize,
                          }).unwrap();
                          setOrderHistoryData(result as OrderData);
                        } catch (error) {
                          // eslint-disable-next-line no-console
                          console.error("Failed to load previous page of order history:", error);
                        }
                      }}
                    >
                      <i className="ri-arrow-left-s-line me-1" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      disabled={
                        orderHistoryPage >= (orderHistoryData.total_pages || 1) ||
                        orderHistoryLoading
                      }
                      onClick={async () => {
                        if (!selectedSupplierForHistory || !productId) return;
                        const nextPage = orderHistoryPage + 1;
                        setOrderHistoryPage(nextPage);
                        try {
                          const result = await getPurchaseOrdersByProductAndSupplier({
                            supplier_id: selectedSupplierForHistory.supplier_id,
                            product_id: productId,
                            page: nextPage,
                            page_size: orderHistoryPageSize,
                          }).unwrap();
                          setOrderHistoryData(result as OrderData);
                        } catch (error) {
                          // eslint-disable-next-line no-console
                          console.error("Failed to load next page of order history:", error);
                        }
                      }}
                    >
                      Next
                      <i className="ri-arrow-right-s-line ms-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="ri-information-line fs-48 text-muted"></i>
              <div className="mt-2 text-muted">No data available</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => toggleOrderHistoryModal()}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SupplierSelectionModal;

